import { Fragment, useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import { Text } from "components";
import { DashboardService } from "services";
import { DashboardConversionHealthResponse, DashboardKpiParams, DashboardScope } from "types";

import classes from "./ConversionSection.module.css";

interface ConversionSectionProps {
  className?: string;
  params: DashboardKpiParams | null;
  scope?: DashboardScope;
}

interface ConversionMetric {
  dataKey: "leadToApproved" | "approvedToDisbursed";
  leftLabelKey: string;
  rightLabelKey: string;
  leftValue: number;
  rightValue: number;
  fillStart: number;
  fillColor: string;
  hasFill: boolean;
}

const EMPTY_CONVERSION_METRICS: readonly ConversionMetric[] = [
  {
    dataKey: "leadToApproved",
    leftLabelKey: "dashboard.admin.operative.metrics.leadsInBank",
    rightLabelKey: "dashboard.admin.operative.metrics.approved",
    leftValue: 0,
    rightValue: 0,
    fillStart: 100,
    fillColor: "#62b644",
    hasFill: false,
  },
  {
    dataKey: "approvedToDisbursed",
    leftLabelKey: "dashboard.admin.operative.metrics.approved",
    rightLabelKey: "dashboard.admin.operative.metrics.disbursed",
    leftValue: 0,
    rightValue: 0,
    fillStart: 100,
    fillColor: "#b24ab4",
    hasFill: false,
  },
] as const;

const getFillStart = (leftValue: number, rightValue: number) => {
  const totalValue = leftValue + rightValue;

  if (totalValue <= 0 || rightValue <= 0) {
    return 100;
  }

  return (leftValue / totalValue) * 100;
};

const mapConversionResponseToMetrics = (
  metrics: readonly ConversionMetric[],
  data: DashboardConversionHealthResponse,
): ConversionMetric[] =>
  metrics.map((metric) => {
    const conversionMetric = data[metric.dataKey];
    const leftValue =
      typeof conversionMetric?.fromCount === "number" ? conversionMetric.fromCount : metric.leftValue;
    const rightValue =
      typeof conversionMetric?.toCount === "number" ? conversionMetric.toCount : metric.rightValue;
    const totalValue = leftValue + rightValue;

    return {
      ...metric,
      leftValue,
      rightValue,
      fillStart: getFillStart(leftValue, rightValue),
      hasFill: totalValue > 0 && rightValue > 0,
    };
  });

const ConversionSection = ({ className, params, scope = "admin" }: ConversionSectionProps) => {
  const { t } = useTranslation();
  const [metrics, setMetrics] = useState<ConversionMetric[]>([...EMPTY_CONVERSION_METRICS]);

  useEffect(() => {
    let mounted = true;

    if (!params) {
      setMetrics([...EMPTY_CONVERSION_METRICS]);
      return () => {
        mounted = false;
      };
    }

    const loadConversionHealth = async () => {
      try {
        const data = await DashboardService.getConversionHealth(params, scope);

        if (!mounted) {
          return;
        }

        setMetrics(mapConversionResponseToMetrics(EMPTY_CONVERSION_METRICS, data));
      } catch {
        if (!mounted) {
          return;
        }

        setMetrics([...EMPTY_CONVERSION_METRICS]);
      }
    };

    loadConversionHealth();

    return () => {
      mounted = false;
    };
  }, [params, scope]);

  return (
    <section className={[classes.root, className].filter(Boolean).join(" ")}>
      <div className={classes.header}>
        <Text className={classes.title}>{t("dashboard.admin.conversion.title")}</Text>
      </div>

      <div className={classes.metrics}>
        {metrics.map((metric) => {
          return (
            <Fragment key={`${metric.leftLabelKey}-${metric.rightLabelKey}`}>
              <Text className={classes.sideLabel}>{t(metric.leftLabelKey)}</Text>

              <div className={classes.track}>
                <div
                  className={classes.fill}
                  style={{
                    left: `${metric.fillStart}%`,
                    backgroundColor: metric.fillColor,
                  }}
                />

                <Text className={classes.leftValue}>{metric.leftValue}</Text>
                <Text
                  className={[
                    classes.rightValue,
                    !metric.hasFill ? classes.rightValueMuted : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                >
                  {metric.rightValue}
                </Text>
              </div>

              <Text className={classes.sideLabel}>{t(metric.rightLabelKey)}</Text>
            </Fragment>
          );
        })}
      </div>
    </section>
  );
};

export default ConversionSection;
